import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { HelpPanel } from '../components/ui/help-panel';
import { journalEntriesHelp } from '../lib/helpContent';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Skeleton } from '../components/ui/skeleton';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
  Plus, 
  Search, 
  CalendarIcon,
  FileText,
  Eye,
  Pencil,
  RotateCcw,
  MoreHorizontal,
  Copy,
  ArrowRightLeft,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Filter,
  X
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(amount);
};

const StatusBadge = ({ status }) => {
  const styles = {
    draft: 'status-draft',
    posted: 'status-posted',
    voided: 'status-voided',
    reversed: 'status-reversed',
  };
  
  return (
    <Badge variant="secondary" className={cn('text-xs capitalize', styles[status])}>
      {status}
    </Badge>
  );
};

// Sort icon component - defined outside main component to avoid React unstable nested component warning
const SortIcon = ({ column, sortColumn, sortDirection }) => {
  if (sortColumn !== column) {
    return <ArrowUpDown className="w-4 h-4 ml-1 opacity-40" />;
  }
  return sortDirection === 'asc' 
    ? <ArrowUp className="w-4 h-4 ml-1" />
    : <ArrowDown className="w-4 h-4 ml-1" />;
};

export default function JournalEntries() {
  const { currentWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  
  // Reverse/Replace dialog state
  const [reverseDialogOpen, setReverseDialogOpen] = useState(false);
  const [replaceDialogOpen, setReplaceDialogOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [processing, setProcessing] = useState(false);
  
  // Sorting state
  const [sortColumn, setSortColumn] = useState('entry_no');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Column filtering state
  const [columnFilters, setColumnFilters] = useState({
    entry_no: '',
    memo: '',
    debit: '',
    credit: ''
  });
  const [showColumnFilters, setShowColumnFilters] = useState(false);

  const fetchEntries = async () => {
    if (!currentWorkspace) return;
    
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/journal-entries`
      );
      
      if (response.ok) {
        const data = await response.json();
        setEntries(data);
      }
    } catch (error) {
      console.error('Failed to fetch entries:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, [currentWorkspace]);

  const handleReverse = async () => {
    if (!selectedEntry || !currentWorkspace) return;
    
    setProcessing(true);
    try {
      const response = await fetch(
        `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/journal-entries/${selectedEntry.entry_id}/reverse`,
        {
          method: 'POST',
          }
      );
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Entry reversed. Reversal entry #${data.entry_no} created and posted.`);
        setReverseDialogOpen(false);
        fetchEntries();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to reverse entry');
      }
    } catch (error) {
      console.error('Failed to reverse entry:', error);
      toast.error('Failed to reverse entry');
    } finally {
      setProcessing(false);
    }
  };

  const handleReplace = async () => {
    if (!selectedEntry || !currentWorkspace) return;
    
    setProcessing(true);
    try {
      const response = await fetch(
        `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/journal-entries/${selectedEntry.entry_id}/replace`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({})
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Entry reversed. Draft replacement #${data.replacement_entry_no} created.`);
        setReplaceDialogOpen(false);
        // Navigate to the new draft entry for editing
        navigate(`/journal-entries/${data.replacement_entry_id}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to replace entry');
      }
    } catch (error) {
      console.error('Failed to replace entry:', error);
      toast.error('Failed to replace entry');
    } finally {
      setProcessing(false);
    }
  };

  const openReverseDialog = (entry) => {
    setSelectedEntry(entry);
    setReverseDialogOpen(true);
  };

  const openReplaceDialog = (entry) => {
    setSelectedEntry(entry);
    setReplaceDialogOpen(true);
  };

  // Filter entries
  const filteredEntries = entries.filter(entry => {
    const matchesSearch = !search || 
      entry.entry_no.toString().includes(search) ||
      (entry.memo && entry.memo.toLowerCase().includes(search.toLowerCase()));
    
    const matchesStatus = statusFilter === 'all' || entry.status === statusFilter;
    
    const matchesStartDate = !startDate || entry.txn_date >= format(startDate, 'yyyy-MM-dd');
    const matchesEndDate = !endDate || entry.txn_date <= format(endDate, 'yyyy-MM-dd');
    
    // Column-specific filters
    const matchesEntryNo = !columnFilters.entry_no || 
      entry.entry_no.toString().includes(columnFilters.entry_no);
    const matchesMemo = !columnFilters.memo || 
      (entry.memo && entry.memo.toLowerCase().includes(columnFilters.memo.toLowerCase()));
    const matchesDebit = !columnFilters.debit || 
      entry.total_debit.toString().includes(columnFilters.debit);
    const matchesCredit = !columnFilters.credit || 
      entry.total_credit.toString().includes(columnFilters.credit);
    
    return matchesSearch && matchesStatus && matchesStartDate && matchesEndDate &&
           matchesEntryNo && matchesMemo && matchesDebit && matchesCredit;
  });

  // Sort entries
  const sortedEntries = [...filteredEntries].sort((a, b) => {
    let aVal, bVal;
    
    switch (sortColumn) {
      case 'entry_no':
        aVal = a.entry_no;
        bVal = b.entry_no;
        break;
      case 'txn_date':
        aVal = a.txn_date;
        bVal = b.txn_date;
        break;
      case 'memo':
        aVal = (a.memo || '').toLowerCase();
        bVal = (b.memo || '').toLowerCase();
        break;
      case 'total_debit':
        aVal = a.total_debit;
        bVal = b.total_debit;
        break;
      case 'total_credit':
        aVal = a.total_credit;
        bVal = b.total_credit;
        break;
      case 'status':
        aVal = a.status;
        bVal = b.status;
        break;
      default:
        aVal = a.entry_no;
        bVal = b.entry_no;
    }
    
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Handle column sort
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Clear all column filters
  const clearColumnFilters = () => {
    setColumnFilters({ entry_no: '', memo: '', debit: '', credit: '' });
  };

  const hasActiveColumnFilters = Object.values(columnFilters).some(v => v !== '');

  // Determine available actions based on status
  const getEntryActions = (entry) => {
    const actions = [];
    
    // View is always available
    actions.push({
      label: 'View',
      icon: Eye,
      href: `/journal-entries/${entry.entry_id}`
    });
    
    if (entry.status === 'draft') {
      actions.push({
        label: 'Edit',
        icon: Pencil,
        href: `/journal-entries/${entry.entry_id}?edit=true`
      });
    }
    
    if (entry.status === 'posted') {
      actions.push({
        label: 'Reverse',
        icon: RotateCcw,
        onClick: () => openReverseDialog(entry)
      });
      actions.push({
        label: 'Replace',
        icon: ArrowRightLeft,
        onClick: () => openReplaceDialog(entry)
      });
    }
    
    return actions;
  };

  return (
    <div className="space-y-6" data-testid="journal-entries-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Journal Entries</h1>
          <p className="text-muted-foreground">
            Double-entry accounting records
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild data-testid="new-entry-btn">
            <Link to="/journal-entries/new">
              <Plus className="w-4 h-4 mr-2" />
              New Entry
            </Link>
          </Button>
          <HelpPanel {...journalEntriesHelp} />
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by entry # or memo..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="entry-search"
              />
            </div>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-40" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="posted">Posted</SelectItem>
                <SelectItem value="voided">Voided</SelectItem>
                <SelectItem value="reversed">Reversed</SelectItem>
              </SelectContent>
            </Select>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="justify-start">
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  {startDate ? format(startDate, 'MMM d, yyyy') : 'Start Date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={startDate}
                  onSelect={setStartDate}
                />
              </PopoverContent>
            </Popover>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="justify-start">
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  {endDate ? format(endDate, 'MMM d, yyyy') : 'End Date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={endDate}
                  onSelect={setEndDate}
                />
              </PopoverContent>
            </Popover>

            {(startDate || endDate) && (
              <Button 
                variant="ghost" 
                onClick={() => { setStartDate(null); setEndDate(null); }}
              >
                Clear Dates
              </Button>
            )}

            <Button
              variant={showColumnFilters ? "secondary" : "outline"}
              onClick={() => setShowColumnFilters(!showColumnFilters)}
              className="gap-2"
              data-testid="toggle-column-filters"
            >
              <Filter className="w-4 h-4" />
              Column Filters
              {hasActiveColumnFilters && (
                <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
                  !
                </Badge>
              )}
            </Button>

            {hasActiveColumnFilters && (
              <Button 
                variant="ghost" 
                onClick={clearColumnFilters}
                className="text-destructive"
              >
                <X className="w-4 h-4 mr-1" />
                Clear Filters
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Entries Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-24">
                  <button
                    onClick={() => handleSort('entry_no')}
                    className="flex items-center hover:text-foreground transition-colors font-medium"
                    data-testid="sort-entry-no"
                  >
                    Entry #
                    <SortIcon column="entry_no" sortColumn={sortColumn} sortDirection={sortDirection} />
                  </button>
                </TableHead>
                <TableHead className="w-28">
                  <button
                    onClick={() => handleSort('txn_date')}
                    className="flex items-center hover:text-foreground transition-colors font-medium"
                    data-testid="sort-date"
                  >
                    Date
                    <SortIcon column="txn_date" sortColumn={sortColumn} sortDirection={sortDirection} />
                  </button>
                </TableHead>
                <TableHead>
                  <button
                    onClick={() => handleSort('memo')}
                    className="flex items-center hover:text-foreground transition-colors font-medium"
                    data-testid="sort-memo"
                  >
                    Memo
                    <SortIcon column="memo" sortColumn={sortColumn} sortDirection={sortDirection} />
                  </button>
                </TableHead>
                <TableHead className="w-28 text-right">
                  <button
                    onClick={() => handleSort('total_debit')}
                    className="flex items-center justify-end w-full hover:text-foreground transition-colors font-medium"
                    data-testid="sort-debit"
                  >
                    Debit
                    <SortIcon column="total_debit" sortColumn={sortColumn} sortDirection={sortDirection} />
                  </button>
                </TableHead>
                <TableHead className="w-28 text-right">
                  <button
                    onClick={() => handleSort('total_credit')}
                    className="flex items-center justify-end w-full hover:text-foreground transition-colors font-medium"
                    data-testid="sort-credit"
                  >
                    Credit
                    <SortIcon column="total_credit" sortColumn={sortColumn} sortDirection={sortDirection} />
                  </button>
                </TableHead>
                <TableHead className="w-24">
                  <button
                    onClick={() => handleSort('status')}
                    className="flex items-center hover:text-foreground transition-colors font-medium"
                    data-testid="sort-status"
                  >
                    Status
                    <SortIcon column="status" sortColumn={sortColumn} sortDirection={sortDirection} />
                  </button>
                </TableHead>
                <TableHead className="w-24 text-right">Actions</TableHead>
              </TableRow>
              {/* Column Filter Row */}
              {showColumnFilters && (
                <TableRow className="bg-muted/30">
                  <TableHead className="p-2">
                    <Input
                      placeholder="Filter..."
                      value={columnFilters.entry_no}
                      onChange={(e) => setColumnFilters(prev => ({ ...prev, entry_no: e.target.value }))}
                      className="h-8 text-xs"
                      data-testid="filter-entry-no"
                    />
                  </TableHead>
                  <TableHead className="p-2">
                    {/* Date filters handled by main filter bar */}
                    <span className="text-xs text-muted-foreground">Use date pickers</span>
                  </TableHead>
                  <TableHead className="p-2">
                    <Input
                      placeholder="Filter memo..."
                      value={columnFilters.memo}
                      onChange={(e) => setColumnFilters(prev => ({ ...prev, memo: e.target.value }))}
                      className="h-8 text-xs"
                      data-testid="filter-memo"
                    />
                  </TableHead>
                  <TableHead className="p-2">
                    <Input
                      placeholder="Filter..."
                      value={columnFilters.debit}
                      onChange={(e) => setColumnFilters(prev => ({ ...prev, debit: e.target.value }))}
                      className="h-8 text-xs"
                      data-testid="filter-debit"
                    />
                  </TableHead>
                  <TableHead className="p-2">
                    <Input
                      placeholder="Filter..."
                      value={columnFilters.credit}
                      onChange={(e) => setColumnFilters(prev => ({ ...prev, credit: e.target.value }))}
                      className="h-8 text-xs"
                      data-testid="filter-credit"
                    />
                  </TableHead>
                  <TableHead className="p-2">
                    {/* Status filter handled by main dropdown */}
                    <span className="text-xs text-muted-foreground">Use dropdown</span>
                  </TableHead>
                  <TableHead className="p-2"></TableHead>
                </TableRow>
              )}
            </TableHeader>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={7}>
                      <Skeleton className="h-10 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              ) : sortedEntries.length > 0 ? (
                sortedEntries.map((entry) => {
                  const actions = getEntryActions(entry);
                  return (
                    <TableRow 
                      key={entry.entry_id}
                      className={cn(
                        (entry.status === 'voided' || entry.status === 'reversed') && 'opacity-50'
                      )}
                      data-testid={`entry-row-${entry.entry_no}`}
                    >
                      <TableCell className="font-mono font-medium">
                        {entry.entry_no}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {entry.txn_date}
                      </TableCell>
                      <TableCell>
                        <p className="truncate max-w-[300px]">
                          {entry.memo || <span className="text-muted-foreground italic">No memo</span>}
                        </p>
                        {entry.source_type && (
                          <Badge variant="outline" className="text-xs mt-1">
                            {entry.source_type}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono debit-amount">
                        {formatCurrency(entry.total_debit)}
                      </TableCell>
                      <TableCell className="text-right font-mono credit-amount">
                        {formatCurrency(entry.total_credit)}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={entry.status} />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {/* Primary action: View */}
                          <Button variant="ghost" size="icon" asChild>
                            <Link 
                              to={`/journal-entries/${entry.entry_id}`}
                              data-testid={`view-entry-${entry.entry_no}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Link>
                          </Button>
                          
                          {/* Edit for drafts */}
                          {entry.status === 'draft' && (
                            <Button variant="ghost" size="icon" asChild>
                              <Link 
                                to={`/journal-entries/${entry.entry_id}?edit=true`}
                                data-testid={`edit-entry-${entry.entry_no}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </Link>
                            </Button>
                          )}
                          
                          {/* More actions for posted entries */}
                          {entry.status === 'posted' && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" data-testid={`entry-actions-${entry.entry_no}`}>
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openReverseDialog(entry)}>
                                  <RotateCcw className="w-4 h-4 mr-2" />
                                  Reverse Entry
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => openReplaceDialog(entry)}>
                                  <ArrowRightLeft className="w-4 h-4 mr-2" />
                                  Reverse & Replace
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12">
                    <FileText className="w-12 h-12 text-muted-foreground/50 mx-auto mb-3" />
                    <p className="text-muted-foreground">No journal entries found</p>
                    <Button variant="outline" size="sm" className="mt-3" asChild>
                      <Link to="/journal-entries/new">Create Your First Entry</Link>
                    </Button>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Reverse Confirmation Dialog */}
      <AlertDialog open={reverseDialogOpen} onOpenChange={setReverseDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reverse Journal Entry?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                This will create a new journal entry that reverses JE #{selectedEntry?.entry_no} by swapping all debits and credits.
              </p>
              <p className="text-sm">
                • The original entry will be marked as "reversed"<br/>
                • A new reversal entry will be created and posted immediately<br/>
                • Ledger balances will be adjusted accordingly
              </p>
              <p className="text-sm font-medium text-amber-600 dark:text-amber-400">
                This action cannot be undone.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={processing}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleReverse} disabled={processing}>
              {processing ? 'Reversing...' : 'Reverse Entry'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Replace Confirmation Dialog */}
      <AlertDialog open={replaceDialogOpen} onOpenChange={setReplaceDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reverse & Replace Entry?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                This will reverse JE #{selectedEntry?.entry_no} and create a new draft replacement entry for you to edit.
              </p>
              <p className="text-sm">
                • The original entry will be marked as "reversed"<br/>
                • A reversal entry will be created and posted<br/>
                • A new draft entry (copy of original) will be created<br/>
                • You'll be redirected to edit the draft
              </p>
              <p className="text-sm font-medium text-amber-600 dark:text-amber-400">
                Use this when you need to correct an error in a posted entry.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={processing}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleReplace} disabled={processing}>
              {processing ? 'Processing...' : 'Reverse & Create Draft'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
